<template>
  <div
    class="language__container"
    :class="{ 'language__container--expanded': showLanguages }"
    @click="toggleLanguages"
  >
    <div class="language__label">
      <span class="language__icon iconfont itranslate"></span>
      <div>{{ languageText }}</div>
      <span
        class="language__icon language__icon--expand iconfont iarrow-down"
      ></span>
    </div>

    <Transition>
      <ul v-if="showLanguages" class="language__list">
        <li
          v-for="language in WEGLOT_LANGS"
          :key="language"
          class="language__item wg-no-translate"
          :class="{
            'language__item--highlighted': language === currentLanguage(),
          }"
        >
          <button class="language__btn" @click="switchLanguage(language)">
            {{ getLanguageName(language) }}
          </button>
        </li>
      </ul>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import {
  WEGLOT_LANGS,
  weglotInitialized,
  getLanguageName,
} from '@/services/weglot'
import { Mixpanel } from '@/analytics/mixpanel'

const showLanguages = ref<boolean>(false)
const languageText = ref<string>('Language')

const toggleLanguages = (): void => {
  showLanguages.value = !showLanguages.value
}

function currentLanguage(): string {
  if (weglotInitialized()) {
    return window.Weglot.getCurrentLang()
  }
  return ''
}

function switchLanguage(language: string): void {
  if (weglotInitialized()) {
    window.Weglot.switchTo(language)
    Mixpanel.trackLanguageSwitch(language)
  }
}
</script>

<style lang="scss" scoped>
.language {
  &__container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    transition: opacity 0.3s ease-in-out;
    cursor: pointer;

    @include hover {
      color: inherit;
    }
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 1px;
    max-height: 200px;
    overflow-y: auto;
    padding: 0;
    interpolate-size: allow-keywords;
  }

  &__icon {
    font-size: 18px;
    line-height: 1;
    width: 18px;
    height: 18px;
  }

  &__icon--expand {
    margin-left: auto;
    transition: all 0.3s;
  }

  &__container--expanded &__icon--expand {
    transform: rotate(-180deg);
  }

  &__btn {
    width: 100%;
    padding: 8px;
    border-radius: 4px;
    background: $c-bg;
    text-align: start;
    transition: all 0.2s;

    @include hover {
      background: rgba($c-black-2, 0.05);
    }
  }

  &__item--highlighted &__btn {
    display: flex;
    justify-content: space-between;
    align-items: center;

    // The checkmark for the selected language.
    &:after {
      content: '';
      display: inline-block;
      height: 12px;
      width: 6px;
      margin-inline: 6px;
      margin-top: -6px;
      border-bottom: 1px solid $c-black-2;
      border-right: 1px solid $c-black-2;
      transform: rotate(45deg);
    }
  }
}

.night-mode .language {
  &__btn {
    background: $c-dark-3;
    color: $c-black-11;

    &:after {
      border-color: $c-black-11;
    }

    @include hover {
      background: $c-dark-2;
    }
  }
}

// Slide in/out transition when expanding/collapsing
// the languages list. Only works on chrome for now.
@supports (interpolate-size: allow-keywords) {
  .v {
    &-enter-active,
    &-leave-active {
      transition: all 0.3s;
      overflow: hidden;
    }

    &-enter-from,
    &-leave-to {
      height: 0;
    }
  }
}
</style>
